import { useState } from 'react';

import { usePagination } from '../../../common/hooks/usePagination';

import { useDebouncedValue } from '../../../common/hooks/useDebouncedValue';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useMaterialGroups, useSaveMaterialGroup } from '../../../hooks/useMaterialGroups';
import { useSearchInventoryArticles } from '../../../hooks/useInventoryArticles';
import {
  GROUP_TEXTS,
  INITIAL_MATERIAL_GROUP_FORM,
  MATERIAL_ARTICLE_SEARCH_MIN_LENGTH,
} from '../constants/materialGroupsConstants';
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage';
import { materialGroupPayload } from '../utils/materialGroupPayload';

/**
 * Hook de la pantalla de grupos: catálogo paginado y ordenable, drawer de edición y baja lógica.
 * @returns {Object} Estado de vista y handlers para la página.
 */
export const useMaterialGroupsPage = () => {
  const [search, setSearch] = useState('');
  const [articleSearch, setArticleSearch] = useState('');
  const { page, setPage, rowsPerPage, handleRowsPerPageChange } = usePagination();
  const [form, setForm] = useState(null);
  const [groupToRemove, setGroupToRemove] = useState(null);
  const [error, setError] = useState('');

  const tableSort = useTableSort(() => setPage(1));
  const debouncedSearch = useDebouncedValue(search);

  const query = useMaterialGroups({
    search: debouncedSearch,
    page,
    per_page: rowsPerPage,
    ...tableSort.sortParams,
  });
  const articlesQuery = useSearchInventoryArticles(articleSearch, {
    enabled: articleSearch.trim().length >= MATERIAL_ARTICLE_SEARCH_MIN_LENGTH,
  });
  const mutation = useSaveMaterialGroup();
  const rawArticles = articlesQuery.data;
  const articleOptions = Array.isArray(rawArticles)
    ? rawArticles
    : rawArticles?.results || rawArticles?.items || [];

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const openCreateDrawer = () => {
    setForm(INITIAL_MATERIAL_GROUP_FORM);
    setError('');
  };

  const openEditDrawer = (group) => {
    setForm({
      uuid: group.uuid,
      code: group.code,
      name: group.name,
      revision: group.revision,
      articles: [...(group.articles || [])],
    });
    setError('');
  };

  const closeDrawer = () => {
    if (mutation.isPending) return;
    setForm(null);
    setArticleSearch('');
    setError('');
  };

  const changeField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addArticle = (option) => {
    const selected = articleOptions.find((article) => article.uuid === option?.value);
    if (!selected) return;
    setForm((prev) => (prev.articles.some((article) => article.uuid === selected.uuid)
      ? prev
      : { ...prev, articles: [...prev.articles, selected] }));
  };

  const removeArticle = (articleId) => {
    setForm((prev) => ({
      ...prev,
      articles: prev.articles.filter((article) => article.uuid !== articleId),
    }));
  };

  const save = async () => {
    try {
      await mutation.mutateAsync({ id: form.uuid, data: materialGroupPayload(form) });
      setForm(null);
      setArticleSearch('');
      setError('');
    } catch (failure) {
      setError(getApiErrorMessage(failure, GROUP_TEXTS.saveError));
    }
  };

  const confirmRemove = async () => {
    try {
      await mutation.mutateAsync({ id: groupToRemove.uuid, remove: true });
      setGroupToRemove(null);
    } catch (failure) {
      setGroupToRemove(null);
      setError(getApiErrorMessage(failure, GROUP_TEXTS.saveError));
    }
  };

  const canSave = Boolean(
    form && form.code.trim() && form.name.trim() && form.articles.length > 0
  );

  return {
    addArticle,
    articleOptions,
    canSave,
    cancelRemove: () => setGroupToRemove(null),
    changeField,
    clearError: () => setError(''),
    closeDrawer,
    confirmRemove,
    drawerOpen: Boolean(form),
    error,
    form,
    groupToRemove,
    groups: query.data?.items || [],
    handlePageChange: (_, value) => setPage(value),
    handleSearch,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    isSearchingArticles: articlesQuery.isFetching,
    loadError: query.isError ? getApiErrorMessage(query.error, GROUP_TEXTS.loadError) : '',
    openCreateDrawer,
    openEditDrawer,
    page,
    rowsPerPage,
    handleRowsPerPageChange,
    refetch: query.refetch,
    removeArticle,
    requestRemove: setGroupToRemove,
    save,
    search,
    setArticleSearch,
    sort: tableSort.sort,
    sortTable: tableSort.toggleSort,
    totalCount: query.data?.total || 0,
  };
};
